import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { createQuickCodeComponent } from 'src/app/module/ptl-master/component/quick-code/create-quick-code/create-quick-code.component';
import { EmsServicesService } from 'src/app/module/ptl-master/services/ems-services.service';
import { AccessControlService } from 'src/app/shared/services/AccessControl.service';
import { TabService } from 'src/app/shared/tab.service';

interface RecentOrder {
  refDocket: string;
  hasDocket: boolean;
  type: 'Docket Manifest' | 'Pre-Docket Manifest';
  customer: string;
  consignee: string;
  vendor: string;
  fromCity: string;
  toCity: string;
  deliveryType: string;
  apptSlot: string;
  vendorStatus: 'Accepted' | 'Pending' | 'Rejected';
}

/** State for one searchable + infinite-scroll dropdown */
interface LookupState {
  items: any[];
  loading: boolean;
  page: number;
  hasMore: boolean;
  term: string;
  input$: Subject<string>;
}

@Component({
  selector: 'app-create-ptl-order',
  templateUrl: './create-ptl-order.component.html',
  styleUrls: ['./create-ptl-order.component.css'],
  providers: [DynamicDialogRef]
})
export class CreatePtlOrderComponent implements OnInit, OnDestroy {

  // ----- Section expand/collapse state -----
  isPickupRequestOpen = true;
  isIntimationOpen = false;
  theme = 'dark';

  warehouseList: any;
  accounts: any;
  UserDetails: any;
  userRole: any;
  userType: any;
  accountIds: unknown[];
  locationIds: unknown[];
  allCatagory: any;
  quickCodeCategoryList: any;
  sendCatagoryId: (number | undefined)[];
  city: any;
  zone: any;
  state: any;
  ref: any;

  isSavingOrder = false;
  isSendingIntimation = false;

  private readonly PAGE_SIZE = 20;
  private readonly SEARCH_DEBOUNCE_MS = 400;
  private destroy$ = new Subject<void>();

  // ----- Searchable, paginated dropdowns (shared by both forms) -----
  customerLookup: LookupState = this.newLookup();
  consigneeLookup: LookupState = this.newLookup();
  vendorLookup: LookupState = this.newLookup();

  togglePickupRequest(): void {
    this.isPickupRequestOpen = !this.isPickupRequestOpen;
  }

  toggleIntimation(): void {
    this.isIntimationOpen = !this.isIntimationOpen;
  }

  // ----- Static dropdown options -----
  orderSources = [
    { label: 'Whatsapp', value: 'WhatsApp' },
    { label: 'Customer Portal', value: 'CustomerPortal' },
    { label: 'Email', value: 'EMAIL' },
    { label: 'Mobile Call', value: 'MobileCall' },
  ];
  intimationOrderSources = [
    { label: 'Customer Portal', value: 'CustomerPortal' },
    { label: 'Whatsapp', value: 'WhatsApp' },
    { label: 'Email', value: 'EMAIL' },
  ];
  createdByOptions = ['Asc', 'Customer'];
  deliveryTypes = ['Normal', 'Appointment'];

  // ----- Form model: New Pickup Request (Docket Manifest) -----
  // customer / consignee / vendor hold the whole selected object (not just the id)
  // so the label stays visible after the dropdown list is refreshed by a search.
  pickupRequestForm: any = this.emptyPickupForm();

  // ----- Form model: Create Intimation (Pre-Docket Manifest) -----
  intimationForm: any = this.emptyIntimationForm();

  // ----- Static: Recent Pickup Requests & Intimations -----
  recentOrders: RecentOrder[] = [
    { refDocket: 'DLV-112233', hasDocket: true, type: 'Docket Manifest', customer: 'Havels', consignee: 'Amazon', vendor: 'Delhivery', fromCity: 'Mumbai', toCity: 'Delhi', deliveryType: 'Appointment', apptSlot: '04 Jun, 10-12 PM', vendorStatus: 'Accepted' },
    { refDocket: 'No docket yet', hasDocket: false, type: 'Pre-Docket Manifest', customer: 'Parle', consignee: 'Zepto', vendor: 'Mudita', fromCity: 'Pune', toCity: 'Kolkata', deliveryType: 'Normal', apptSlot: '-', vendorStatus: 'Pending' },
    { refDocket: 'GTI-445566', hasDocket: true, type: 'Docket Manifest', customer: 'Samsung', consignee: 'Flipkart', vendor: 'Gati', fromCity: 'Chennai', toCity: 'Hyderabad', deliveryType: 'Normal', apptSlot: '-', vendorStatus: 'Rejected' },
    { refDocket: 'BD-778899', hasDocket: true, type: 'Docket Manifest', customer: 'Nestle', consignee: 'Walmart', vendor: 'Blue Dart', fromCity: 'Bengaluru', toCity: 'Ahmedabad', deliveryType: 'Appointment', apptSlot: '06 Jun, 2-4 PM', vendorStatus: 'Accepted' },
    { refDocket: 'No docket yet', hasDocket: false, type: 'Pre-Docket Manifest', customer: 'ITC', consignee: 'Amazon', vendor: 'Ecom Express', fromCity: 'Kolkata', toCity: 'Lucknow', deliveryType: 'Normal', apptSlot: '-', vendorStatus: 'Pending' }
  ];

  constructor(public dialogService: DialogService, private emsServices: EmsServicesService, public messageService: MessageService,
    public dialogRef: DynamicDialogRef,
    private tabService: TabService, private router: Router, public accessControlService: AccessControlService) {
    this.warehouseList = JSON.parse(localStorage.getItem('wareHouseList')!);
    this.accounts = JSON.parse(localStorage.getItem('accountList')!);
    this.UserDetails = JSON.parse(localStorage.getItem('UserDetails')!);
    this.userRole = this.UserDetails?.roleName;
    this.userType = this.UserDetails?.userType;
    this.allCatagory = JSON.parse(localStorage.getItem('categoryMap')!);
    this.quickCodeCategoryList = JSON.parse(localStorage.getItem('category')!);
    this.sendCatagoryId = this.setId();
    this.getQuickCodeListByCategoryId(this.sendCatagoryId.toString());
    this.accountIds = Array.from(new Set(this.UserDetails.accountMapping.map((m: any) => m.accountId)));
    this.locationIds = Array.from(new Set(this.UserDetails.accountMapping.map((m: any) => m.accountLocationMappingId)));
  }

  ngOnInit(): void {
    // Each call wires: typeahead (debounced) -> API page 0. The first emission ('') loads the initial list.
    this.setupLookup(this.customerLookup, (term, page) =>
      this.emsServices.getAccounts(this.buildAccountFilter('Customer', term), page, this.PAGE_SIZE, 'Id', 'ASC'));

    this.setupLookup(this.consigneeLookup, (term, page) =>
      this.emsServices.getAccounts(this.buildAccountFilter('Consignee', term), page, this.PAGE_SIZE, 'Id', 'ASC'));

    this.setupLookup(this.vendorLookup, (term, page) =>
      this.emsServices.getVendorInfo(this.buildVendorFilter(term), page, this.PAGE_SIZE, 'Id', 'ASC'));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ==================== Lookup helpers (debounce + infinite scroll) ====================

  private newLookup(): LookupState {
    return { items: [], loading: false, page: 0, hasMore: true, term: '', input$: new Subject<string>() };
  }

  private buildAccountFilter(serviceType: 'Customer' | 'Consignee', term: string): any[] {
    const filter: any[] = [{ key: 'SERVICE_TYPE', value: serviceType }];
    if (this.userRole !== 'ROLE_SUPERUSER' && this.accountIds?.length > 0) {
      filter.push({ key: 'ACCOUNT_ID_IN', value: this.accountIds.join(',') });
    }
    if (term) {
      filter.push({ key: 'CUSTOMER_NAME_IN', value: term });
    }
    return filter;
  }

  private buildVendorFilter(term: string): any[] {
    // NOTE: confirm the exact key name for vendor search with backend
    return term ? [{ key: 'VENDOR_NAME_IN', value: term }] : [];
  }

  private setupLookup(state: LookupState, fetch: (term: string, page: number) => Observable<any>): void {
    state.input$.pipe(
      debounceTime(this.SEARCH_DEBOUNCE_MS),
      startWith(''),                       // initial load without waiting for debounce
      distinctUntilChanged(),
      tap(term => {
        state.term = (term ?? '').trim();
        state.page = 0;
        state.hasMore = true;
        state.items = [];
        state.loading = true;
      }),
      switchMap(() => fetch(state.term, 0).pipe(catchError(() => of(null)))),
      takeUntil(this.destroy$)
    ).subscribe(res => this.applyPage(state, res, false));
  }

  /** (scrollToEnd) handler: fetch next page and append */
  loadMore(state: LookupState): void {
    if (state.loading || !state.hasMore) { return; }

    const term = state.term;
    const nextPage = state.page + 1;
    state.loading = true;

    this.fetcherFor(state)(term, nextPage).pipe(
      catchError(() => of(null)),
      takeUntil(this.destroy$)
    ).subscribe(res => {
      if (term !== state.term) { return; }   // user typed something new meanwhile; ignore stale page
      state.page = nextPage;
      this.applyPage(state, res, true);
    });
  }

  private fetcherFor(state: LookupState): (term: string, page: number) => Observable<any> {
    if (state === this.customerLookup) {
      return (t, p) => this.emsServices.getAccounts(this.buildAccountFilter('Customer', t), p, this.PAGE_SIZE, 'Id', 'ASC');
    }
    if (state === this.consigneeLookup) {
      return (t, p) => this.emsServices.getAccounts(this.buildAccountFilter('Consignee', t), p, this.PAGE_SIZE, 'Id', 'ASC');
    }
    return (t, p) => this.emsServices.getVendorInfo(this.buildVendorFilter(t), p, this.PAGE_SIZE, 'Id', 'ASC');
  }

  private applyPage(state: LookupState, res: any, append: boolean): void {
    const ro = res?.responseObject;
    const content: any[] = ro?.content ?? [];
    state.items = append ? [...state.items, ...content] : content;
    state.hasMore = ro?.last !== undefined ? !ro.last : content.length === this.PAGE_SIZE;
    state.loading = false;
  }

  // ==================== Quick codes (city / state / zone) ====================

  setId(): (number | undefined)[] {
    const keys = ['city', 'state', 'zone'];
    return keys.map((key) => this.allCatagory?.[key]?.id);
  }

  getQuickCodeListByCategoryId(ids: any) {
    this.emsServices
      .getQuickCodeListByCategoryId(ids)
      .subscribe((data: any) => {
        this.city = data?.responseObject?.city;
        this.zone = data?.responseObject?.zone;
        this.state = data?.responseObject?.state;
      });
  }

  /** name: 'city' | 'state' | 'zone'; select: template ref of the ng-select to close */
  create(name: 'city' | 'state' | 'zone', select?: any) {
    document.querySelector('.ng-dropdown-panel')?.classList.remove('ng-select-opened');
    select?.close();

    const selectedData = [
      {
        categoryId: this.allCatagory[name].id,
        status: this.allCatagory[name].status
      }
    ];
    this.ref = this.dialogService.open(createQuickCodeComponent, {
      header: 'Add Quick Code',
      data: {
        quickCodeCategoryList: this.quickCodeCategoryList,
        editData: selectedData,
        addFromAnotherComponent: true
      },
      width: '35vw',
      contentStyle: { overflow: 'auto', position: 'top' },
      draggable: true,
    });
    this.ref.onClose.subscribe((data: any) => {
      if (data?.responseObject) {
        setTimeout(() => {
          this.sendCatagoryId = this.setId();
          this.getQuickCodeListByCategoryId(this.sendCatagoryId.toString());
        }, 500);
      }
    });
  }

  // ==================== Form defaults ====================

  private emptyPickupForm(): any {
    return {
      orderSource: 'WHATSAPP',
      customer: null,
      consignee: null,
      createdBy: 'ASC',
      pickupAddress: '',
      deliveryAddress: '',
      fromCityId: null,
      fromStateId: null,
      fromPincode: '',
      toCityId: null,
      toStateId: null,
      toPincode: '',
      fromZoneId: null,
      toZoneId: null,
      actualWeight: 0,
      volWeight: 0,
      noOfBoxes: 1,
      poNumber: '',
      vendor: null,
      deliveryType: 'Normal',
      vendorDocketNo: '',
      // Appointment slot (used only when deliveryType === 'Appointment')
      slotFrom: '',            // datetime-local string, e.g. 2026-10-16T15:00
      slotTo: '',
      appointmentRemarks: ''
    };
  }

  private emptyIntimationForm(): any {
    return {
      customer: null,
      vendor: null,
      consignee: null,
      orderSource: 'CustomerPortal',
      pickupAddress: '',
      deliveryAddress: '',
      fromCityId: null,
      fromStateId: null,
      fromPincode: '',
      toCityId: null,
      toStateId: null,
      toPincode: '',
      approxWeight: 0,
      noOfBoxes: 1,
      deliveryType: 'Normal',
      remarks: ''
    };
  }

  // ==================== Appointment helpers ====================

  /** Clears slot values when the user switches back to a non-appointment delivery type */
  onDeliveryTypeChange(value: string): void {
    if (value !== 'Appointment') {
      this.pickupRequestForm.slotFrom = '';
      this.pickupRequestForm.slotTo = '';
      this.pickupRequestForm.appointmentRemarks = '';
    }
  }

  // /** '2026-10-16T15:00' -> '16-10-2026' */
  // private toApiDate(dt: string): string {
  //   const [y, m, d] = dt.split('T')[0].split('-');
  //   return `${d}-${m}-${y}`;
  // }

  /** '2026-10-21T14:10' -> '21-10-2026 14:10:00 +0530' (offset taken from the browser's timezone) */
  private toApiDateTime(dt: string): string {
    if (!dt) { return ''; }
    const [date, time = ''] = dt.split('T');
    const [y, m, d] = date.split('-');
    const offsetMin = -new Date(dt).getTimezoneOffset();
    const sign = offsetMin >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMin);
    const oh = String(Math.floor(abs / 60)).padStart(2, '0');
    const om = String(abs % 60).padStart(2, '0');
    return `${d}-${m}-${y} ${time.substring(0, 5)}:00 ${sign}${oh}${om}`;
  }

  // ==================== Actions ====================

  saveDraft(): void {
    console.log('Save Draft clicked', this.pickupRequestForm);
  }

  sendToVendor(): void {
    console.log('Send to Vendor clicked', this.pickupRequestForm);
  }

  private toApiDate(dt: string): string {
    const [y, m, d] = dt.split('T')[0].split('-');
    return `${d}-${m}-${y}`;
  }
  
  /** '2026-10-16T15:00' -> '15:00' */
  private toApiTime(dt: string): string {
    return dt.split('T')[1]?.substring(0, 5) ?? '';
  }

  /** Docket Manifest -> requestType: 'PickupRequest' */
  createOrder(): void {
    const f = this.pickupRequestForm;

    const missing = this.missingFields([
      ['Customer', f.customer], ['Consignee', f.consignee], ['Vendor', f.vendor],
      ['Pickup Address', f.pickupAddress], ['Delivery Address', f.deliveryAddress],
      ['From City', f.fromCityId], ['From Pincode', f.fromPincode],
      ['To City', f.toCityId], ['To Pincode', f.toPincode],
    ]);
    if (missing.length) {
      this.warn(`Please fill: ${missing.join(', ')}`);
      return;
    }

    const isAppointment = f.deliveryType === 'Appointment';

    // Slots are optional; if both are given, "to" must be after "from"
    if (isAppointment && f.slotFrom && f.slotTo && f.slotTo <= f.slotFrom) {
      this.warn('Slot To must be later than Slot From');
      return;
    }

    const payload = {
      accountId: this.accountIds?.[0] ?? f.customer.id,
      customerId: f.customer.id,
      vendorId: f.vendor.id,
      consigneeId: f.consignee.id,
      orderCreatedBy: f.createdBy,
      orderSource: f.orderSource,
      fromAddress: f.pickupAddress,
      fromCityId: f.fromCityId,
      fromPincode: String(f.fromPincode),
      fromZoneId: f.fromZoneId,
      toAddress: f.deliveryAddress,
      toCityId: f.toCityId,
      toPincode: String(f.toPincode),
      toZoneId: f.toZoneId,
      weight: Number(f.actualWeight) || 0,
      volumetricWeight: Number(f.volWeight) || 0,
      boxes: Number(f.noOfBoxes) || 0,
      poNumber: f.poNumber,
      docketNumber: f.vendorDocketNo,
      deliveryType: f.deliveryType,
      requestType: 'PickupRequest',
      ...(isAppointment && {
        appointmentDate: this.toApiDate(f.slotFrom),   // "16-10-2026"
        slotFrom: this.toApiTime(f.slotFrom),          // "15:00"
        slotTo: this.toApiTime(f.slotTo),              // "17:00"
        appointmentRemarks: f.appointmentRemarks
      })
    };

    this.isSavingOrder = true;
    this.emsServices.addOrUpdatePickupRequest(payload).subscribe({
      next: (res: any) => {
        this.isSavingOrder = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: res?.message || 'Order created successfully' });
        this.pickupRequestForm = this.emptyPickupForm();
        this.router.navigate(['/orders'])
      },
      error: (err: any) => {
        this.isSavingOrder = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'Failed to create order' });
      }
    });
  }

  /** Pre-Docket Manifest -> requestType: 'Intimation' */
  sendIntimation(): void {
    const f = this.intimationForm;

    const missing = this.missingFields([
      ['Customer', f.customer], ['Vendor', f.vendor],
      ['Pickup Address', f.pickupAddress], ['Delivery Address', f.deliveryAddress],
      ['From City', f.fromCityId], ['From Pincode', f.fromPincode],
      ['To City', f.toCityId], ['To Pincode', f.toPincode],
    ]);
    if (missing.length) {
      this.warn(`Please fill: ${missing.join(', ')}`);
      return;
    }

    const payload = {
      accountId: this.accountIds?.[0] ?? f.customer.id,
      customerId: f.customer.id,
      vendorId: f.vendor.id,
      consigneeId: f.consignee?.id,
      orderSource: f.orderSource,
      fromAddress: f.pickupAddress,
      fromCityId: f.fromCityId,
      fromPincode: String(f.fromPincode),
      toAddress: f.deliveryAddress,
      toCityId: f.toCityId,
      toPincode: String(f.toPincode),
      weight: Number(f.approxWeight) || 0,
      boxes: Number(f.noOfBoxes) || 0,
      deliveryType: f.deliveryType,
      requestType: 'Intimation',
      remarks: f.remarks
    };

    this.isSendingIntimation = true;
    this.emsServices.addOrUpdatePickupRequest(payload).subscribe({
      next: (res: any) => {
        this.isSendingIntimation = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: res?.message || 'Intimation sent successfully' });
        this.intimationForm = this.emptyIntimationForm();
        this.router.navigate(['/orders'])
      },
      error: (err: any) => {
        this.isSendingIntimation = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'Failed to send intimation' });
      }
    });
  }

  viewAllOrders(): void {
    console.log('View All Orders clicked');
  }

  goBack(): void {
    this.router.navigate(['/orders']);
  }

  onRowAction(action: string, order: RecentOrder): void {
    console.log(action, order);
  }

  private missingFields(pairs: [string, any][]): string[] {
    return pairs
      .filter(([, v]) => v === null || v === undefined || (typeof v === 'string' && !v.trim()))
      .map(([label]) => label);
  }

  private warn(detail: string): void {
    this.messageService.add({ severity: 'warn', summary: 'Validation', detail });
  }
}